import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { PlannoMark } from '~/components/brand/planno-mark'
import { APP_MOCKUP_SCREENS, AppMockup } from '~/components/marketing/app-mockup'
import {
  AiMockup,
  ChecklistMockup,
  FeedMockup,
  InboxMockup,
  VisitMockup,
} from '~/components/marketing/feature-mockups'
import {
  ChecklistEstadosMockup,
  ChecklistEvidenciaMockup,
  FeedMediaMockup,
  FeedRealMockup,
  IaConversaMockup,
  IaRenderMockup,
  IaRetroMockup,
  InboxRealMockup,
  VisitaCoresMockup,
  VisitaFotosMockup,
} from '~/components/marketing/mockups/features'
import {
  ArquivosMockup,
  BriefingMockup,
  CalendarioMockup,
  ClienteContextoMockup,
  ContratoMockup,
  DocumentoMockup,
  EmailMockup,
  FunilMockup,
  PortalMockup,
  PropostaPublicaMockup,
  RelatorioMockup,
  TarefasQuadroMockup,
  VisitasAgendaMockup,
  WhatsAppMockup,
} from '~/components/marketing/mockups/modules'
import {
  BriefingsFluxoScreen,
  BriefingsLinksScreen,
  ContatosContextoScreen,
  ContatosTabelaScreen,
  EquipeCargaScreen,
  EquipePessoasScreen,
  FinanceiroPainelScreen,
  FinanceiroProjetosScreen,
  IaCentralScreen,
  IaFontesScreen,
  InicioHibridoScreen,
  InicioPainelScreen,
  ProjetosQuadroScreen,
  ProjetosTabelaScreen,
  PropostasKanbanScreen,
  PropostasTabelaScreen,
  SprintsFielScreen,
  SprintsRetroScreen,
  TarefasListaScreen,
  TarefasQuadroRealScreen,
} from '~/components/marketing/mockups/screens'
import {
  AppDuoMockup,
  AppFielMockup,
  AppNavegadorMockup,
} from '~/components/marketing/mockups/shells'
import { WorkspaceMockup } from '~/components/marketing/mockups/workspace-mockup'
import { PranchetaPage, SERIF } from '~/components/marketing/prancheta-kit'
import { MarketingLocaleProvider } from '~/lib/marketing-locale'
import { cn } from '~/lib/utils'

/*
 * Mockup workshop — a TEMPORARY page to compare product-mockup directions
 * before committing one to the homepage. Two axes under test:
 *
 *   1. Structure: abstract placeholder bars → real content and states.
 *   2. Media: color-block "photos" → real photographs and renders.
 *
 * Plus a gallery of NEW mockup concepts for product surfaces the marketing
 * site never shows today. Not linked, noindexed, delete when decided.
 */

export const Route = createFileRoute('/lp/mockups')({
  component: RouteComponent,
  head: () => ({
    meta: [
      { title: 'Planno — oficina de mockups' },
      { content: 'noindex, nofollow', name: 'robots' },
    ],
  }),
})

const LABEL_TONES = {
  aspiracional: 'bg-[#a97e22] text-[#fdf9ee]',
  atual: 'bg-[#e9e6df] text-[#6f6a5c]',
  novo: 'bg-[#41608a] text-[#f6f9fd]',
  variação: 'bg-[#b8552e] text-[#fbf6ec]',
} as const

/** One cell of the gallery: label chip + mockup + rationale. */
const Variant = ({
  children,
  chosen,
  className,
  label,
  note,
  title,
}: {
  children: ReactNode
  /** Anthony's pick from the review round. */
  chosen?: boolean
  className?: string
  label: keyof typeof LABEL_TONES
  note?: string
  title: string
}) => (
  <div className={cn('flex flex-col', className)}>
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <span
        className={cn(
          'rounded-full px-2.5 py-0.5 font-semibold text-[10.5px] uppercase tracking-[0.08em]',
          LABEL_TONES[label]
        )}
      >
        {label}
      </span>
      <span className="font-semibold text-[#29251e] text-[14px]">{title}</span>
      {chosen ? (
        <span className="rounded-full bg-[#3e7d33] px-2.5 py-0.5 font-semibold text-[#f0f7ec] text-[10.5px] uppercase tracking-[0.08em]">
          ✓ escolhido
        </span>
      ) : null}
    </div>
    <div>{children}</div>
    {note ? <p className="mt-3 text-[#6f6a5c] text-[13px] leading-relaxed">{note}</p> : null}
  </div>
)

/** The main mockup's content-pane frame: every inner page is judged at the
 * exact size it will render inside the shell. */
const ScreenBox = ({ children }: { children: ReactNode }) => (
  <div className="h-[520px] max-w-[820px] overflow-hidden rounded-2xl border border-[#ececec] bg-white text-left shadow-[0_18px_50px_-20px_rgba(0,0,0,0.18)]">
    {children}
  </div>
)

type InnerPage = {
  atualLabel: keyof typeof LABEL_TONES
  atualNote: string
  id: string
  title: string
  variants: {
    C: () => ReactNode
    label: keyof typeof LABEL_TONES
    note: string
    title: string
  }[]
}

const INNER_PAGES: InnerPage[] = [
  {
    atualLabel: 'aspiracional',
    atualNote: 'O feed "Para você" — não existe no app; a página Atualizações é um placeholder.',
    id: 'home',
    title: '08.1 · Início',
    variants: [
      {
        C: InicioPainelScreen,
        label: 'variação',
        note: 'O Início REAL: KPIs, funil por estágio, sprint atual e Minhas tarefas — o que o usuário de fato encontra.',
        title: 'Painel real',
      },
      {
        C: InicioHibridoScreen,
        label: 'aspiracional',
        note: 'Híbrido: os KPIs reais em cima, o feed embaixo — vende o painel E a colaboração.',
        title: 'Painel + feed',
      },
    ],
  },
  {
    atualLabel: 'atual',
    atualNote: 'Tabela simples com status e prazos.',
    id: 'projects',
    title: '08.2 · Projetos',
    variants: [
      {
        C: ProjetosQuadroScreen,
        label: 'variação',
        note: 'O quadro por etapa de execução (Levantamento → Obra), com cards de arquitetura: tipo, m², tarefas.',
        title: 'Quadro por etapa',
      },
      {
        C: ProjetosTabelaScreen,
        label: 'variação',
        note: 'A tabela crescida: capa (render do projeto), pill de etapa e barra de progresso.',
        title: 'Tabela com capas',
      },
    ],
  },
  {
    atualLabel: 'atual',
    atualNote: 'Kanban de 3 colunas com status genéricos (Backlog / Em andamento / Concluído).',
    id: 'tasks',
    title: '08.3 · Tarefas',
    variants: [
      {
        C: TarefasQuadroRealScreen,
        label: 'variação',
        note: 'O quadro real: Backlog / A fazer / Em andamento / Em revisão, chip de fase de obra, prioridade e o seletor de vistas.',
        title: 'Quadro real',
      },
      {
        C: TarefasListaScreen,
        label: 'variação',
        note: 'A vista Lista, agrupada por status — a densidade do uso diário.',
        title: 'Lista agrupada',
      },
    ],
  },
  {
    atualLabel: 'atual',
    atualNote: 'Meta, contadores e burndown — estrutura próxima do real.',
    id: 'sprints',
    title: '08.4 · Sprints',
    variants: [
      {
        C: SprintsFielScreen,
        label: 'variação',
        note: 'Fiel à página real: linhas de sprint com donut, KPIs (Concluído/Escopo/Trazidas/Ciclo) e burndown.',
        title: 'Lista fiel',
      },
      {
        C: SprintsRetroScreen,
        label: 'variação',
        note: 'O sprint ativo ao lado da retro do anterior — planejado × entregue na mesma tela.',
        title: 'Sprint + retro',
      },
    ],
  },
  {
    atualLabel: 'atual',
    atualNote: 'Tabela de envios. No app real, briefings são LINKS gerados por template.',
    id: 'briefings',
    title: '08.5 · Briefings',
    variants: [
      {
        C: BriefingsLinksScreen,
        label: 'variação',
        note: 'Fiel: os links por template (Gerar/Copiar/Revogar) e as respostas recentes.',
        title: 'Links e respostas',
      },
      {
        C: BriefingsFluxoScreen,
        label: 'variação',
        note: 'A história: a resposta da cliente virando lead na coluna Novo — com as fotos de referência.',
        title: 'Resposta → lead',
      },
    ],
  },
  {
    atualLabel: 'atual',
    atualNote: 'Kanban Rascunho / Enviada / Aceita, sem nº nem rastro do cliente.',
    id: 'proposals',
    title: '08.6 · Propostas',
    variants: [
      {
        C: PropostasTabelaScreen,
        label: 'variação',
        note: 'A tabela real: nº, etapa, total, validade e o olho azul de "aberta pelo cliente".',
        title: 'Tabela real',
      },
      {
        C: PropostasKanbanScreen,
        label: 'variação',
        note: 'O kanban crescido: nº da proposta, validade e o olho no card + pipeline no topo.',
        title: 'Kanban completo',
      },
    ],
  },
  {
    atualLabel: 'atual',
    atualNote: 'Lista com papel e projeto — próxima da página Clientes real.',
    id: 'contacts',
    title: '08.7 · Contatos',
    variants: [
      {
        C: ContatosTabelaScreen,
        label: 'variação',
        note: 'A tabela real de Clientes: tipo, email, telefone, atualizado.',
        title: 'Tabela real',
      },
      {
        C: ContatosContextoScreen,
        label: 'variação',
        note: 'Com contexto vivo: projeto em andamento, dinheiro em jogo e o próximo follow-up por contato.',
        title: 'Com contexto',
      },
    ],
  },
  {
    atualLabel: 'aspiracional',
    atualNote: 'Financeiro ainda não existe no app.',
    id: 'finance',
    title: '08.8 · Financeiro',
    variants: [
      {
        C: FinanceiroPainelScreen,
        label: 'aspiracional',
        note: 'Painel: KPIs, recebíveis do mês e entradas por mês.',
        title: 'Painel',
      },
      {
        C: FinanceiroProjetosScreen,
        label: 'aspiracional',
        note: 'Por projeto: o plano de parcelas de cada contrato, com a atrasada em vermelho.',
        title: 'Por projeto',
      },
    ],
  },
  {
    atualLabel: 'aspiracional',
    atualNote: 'A conversa é boa, mas a página de IA ainda não existe.',
    id: 'ai',
    title: '08.9 · IA',
    variants: [
      {
        C: IaFontesScreen,
        label: 'aspiracional',
        note: 'A conversa com o rail de fontes — mostra que a resposta vem do projeto, não do nada.',
        title: 'Conversa + fontes',
      },
      {
        C: IaCentralScreen,
        label: 'aspiracional',
        note: 'Central de ações: retro, termo, resumo semanal e o render antes/depois — IA como rotina, não chat.',
        title: 'Central de ações',
      },
    ],
  },
  {
    atualLabel: 'atual',
    atualNote: 'Cards de membros. No app real, equipe vive em Configurações › Pessoas.',
    id: 'team',
    title: '08.10 · Equipe',
    variants: [
      {
        C: EquipePessoasScreen,
        label: 'variação',
        note: 'Fiel a Configurações › Pessoas: papéis, atividade e o convite pendente.',
        title: 'Pessoas',
      },
      {
        C: EquipeCargaScreen,
        label: 'variação',
        note: 'Carga da semana por pessoa — quem está com a mão cheia, em uma olhada.',
        title: 'Carga de trabalho',
      },
    ],
  },
]

const Concept = ({
  children,
  intro,
  title,
}: {
  children: ReactNode
  intro: string
  title: string
}) => (
  <section className="border-[#29251e]/12 border-t py-16 first:border-t-0">
    <div className="mx-auto max-w-[1240px] px-6">
      <h2 className={cn('font-semibold text-[clamp(24px,3vw,34px)] tracking-[-0.01em]', SERIF)}>
        {title}
      </h2>
      <p className="mt-2 max-w-[680px] text-[#5f594c] text-[15px] leading-relaxed">{intro}</p>
      <div className="mt-10 grid items-start gap-x-8 gap-y-12 md:grid-cols-2 xl:grid-cols-3">
        {children}
      </div>
    </div>
  </section>
)

function RouteComponent() {
  return (
    <MarketingLocaleProvider locale="pt-BR">
      <PranchetaPage>
        {/* Minimal workshop chrome */}
        <header className="relative z-20 border-[#29251e]/12 border-b bg-[#faf8f2]/90">
          <div className="mx-auto flex h-[64px] max-w-[1240px] items-center justify-between px-6">
            <a className="flex items-center gap-2" href="/lp/prancheta">
              <PlannoMark glyphOnly />
              <span className={cn('font-semibold text-[20px]', SERIF)}>Planno</span>
              <span className="ml-2 rounded-full bg-[#29251e]/8 px-2.5 py-1 font-medium text-[#6f6a5c] text-[11px] uppercase tracking-[0.1em]">
                oficina de mockups
              </span>
            </a>
            <a className="text-[#6f6a5c] text-[13.5px] hover:text-[#29251e]" href="/lp/prancheta">
              ← voltar à homepage
            </a>
          </div>
        </header>

        <main className="relative z-10 pb-24">
          <div className="mx-auto max-w-[1240px] px-6 pt-14 pb-4">
            <h1
              className={cn(
                'max-w-[720px] font-semibold text-[clamp(32px,4vw,48px)] leading-[1.06] tracking-[-0.015em]',
                SERIF
              )}
            >
              Como o produto deve aparecer no site?
            </h1>
            <p className="mt-4 max-w-[680px] text-[#5f594c] text-[16px] leading-relaxed">
              Cada seção compara o mockup <strong className="font-semibold">atual</strong> com
              variações que sobem a fidelidade em dois eixos: estrutura (barras abstratas → conteúdo
              real, estados reais) e mídia (blocos de cor → fotos e renders de verdade). No fim, uma
              galeria de mockups novos para partes do produto que o site ainda não mostra. O selo{' '}
              <span className="rounded-full bg-[#a97e22] px-2 py-0.5 font-semibold text-[#fdf9ee] text-[11px] uppercase tracking-[0.06em]">
                aspiracional
              </span>{' '}
              marca superfícies que ainda não existem no app (feed, WhatsApp, financeiro) — mostrar
              como screenshot é uma decisão, não um fato.
            </p>
          </div>

          <Concept
            intro="O feed é o argumento da segunda-feira sem reunião de status — mas atenção: essa superfície ainda não existe no app (a página Atualizações é um placeholder). Nas variações, os posts ganham autor, projeto, texto legível e anexos."
            title="01 · Feed do projeto"
          >
            <Variant
              label="atual"
              note="Barras abstratas: transmite 'feed', mas não conta nenhuma história."
              title="Feed"
            >
              <FeedMockup />
            </Variant>
            <Variant
              label="aspiracional"
              note="Conteúdo real e reações: dá para LER o produto funcionando."
              title="Conteúdo real"
            >
              <FeedRealMockup />
            </Variant>
            <Variant
              chosen
              label="aspiracional"
              note="Posts carregam anexos — checklist com progresso e fotos reais da obra."
              title="Com mídia"
            >
              <FeedMediaMockup />
            </Variant>
          </Concept>

          <Concept
            intro="A visita técnica é o momento mais físico do produto — o teste perfeito para o eixo 'cor vs foto'. Mesmo card, duas mídias."
            title="02 · Visita técnica"
          >
            <Variant
              label="atual"
              note="Os blocos de cor de hoje: bonito, mas genérico."
              title="Visita"
            >
              <VisitMockup />
            </Variant>
            <Variant
              label="variação"
              note="Mesma estrutura com conteúdo real, ainda em blocos de cor."
              title="Conteúdo real, sem fotos"
            >
              <VisitaCoresMockup />
            </Variant>
            <Variant
              chosen
              label="variação"
              note="Fotos reais + pendência sinalizada: parece um produto que já existe."
              title="Fotos reais"
            >
              <VisitaFotosMockup />
            </Variant>
          </Concept>

          <Concept
            intro="O checklist de entrega é o mockup que mais aparece (homepage e recursos). As variações mostram estados de verdade — feito, em andamento, não conforme — e evidências fotográficas com assinatura."
            title="03 · Checklist de obra"
          >
            <Variant
              label="atual"
              note="Só feito/não feito, sem fotos nem assinatura."
              title="Recebimento de obra"
            >
              <ChecklistMockup />
            </Variant>
            <Variant
              label="variação"
              note="Progresso no topo, três estados por item, contagem de fotos."
              title="Estados reais"
            >
              <ChecklistEstadosMockup />
            </Variant>
            <Variant
              chosen
              label="variação"
              note="Item não conforme com evidência fotográfica + assinatura e relatório."
              title="Evidência e assinatura"
            >
              <ChecklistEvidenciaMockup />
            </Variant>
          </Concept>

          <Concept
            intro="A IA de hoje é uma lista de frases. As variações mostram a conversa real com o rascunho editável, a retrospectiva 'planejado versus entregue', e o render gerado a partir da foto da obra — o antes/depois mais vendedor que temos."
            title="04 · Planno IA"
          >
            <Variant label="atual" note="Não dá para saber o que a IA faz." title="Retrospectiva">
              <AiMockup />
            </Variant>
            <Variant
              chosen
              label="variação"
              note="A conversa real do produto, com rascunho de documento no fim."
              title="Conversa + rascunho"
            >
              <IaConversaMockup />
            </Variant>
            <Variant
              label="variação"
              note="Planejado × entregue com ✓ e ✗ — a retro em uma imagem."
              title="Planejado × entregue"
            >
              <IaRetroMockup />
            </Variant>
            <Variant
              label="variação"
              note="Foto da obra → render: o antes/depois que ninguém mais oferece."
              title="Render a partir da obra"
            >
              <IaRenderMockup />
            </Variant>
          </Concept>

          <Concept
            intro="A caixa de entrada com linhas de verdade: quem comentou, em quê, o que está por resolver."
            title="05 · Caixa de entrada"
          >
            <Variant label="atual" note="Estrutura certa, conteúdo vazio." title="Inbox">
              <InboxMockup />
            </Variant>
            <Variant
              chosen
              label="variação"
              note="Notificações e thread reais, com ação de resolver à vista."
              title="Conversa real"
            >
              <InboxRealMockup />
            </Variant>
          </Concept>

          <Concept
            intro="Superfícies do produto que o site ainda não mostra — candidatas a novas seções da homepage ou das páginas de recursos. Todas usam o mesmo escritório fictício."
            title="06 · Mockups novos"
          >
            <Variant
              chosen
              className="md:col-span-2"
              label="novo"
              note="Fiel ao app: faixa de estatísticas, estágios reais, origem do lead e a zona de Ganho."
              title="Funil de clientes"
            >
              <FunilMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="O que o CLIENTE vê no link público, no design Clássica: papel timbrado, tabela de valores, aceite eletrônico."
              title="Proposta pública"
            >
              <PropostaPublicaMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="Fiel ao quadro real: chip de fase de obra, prioridade, datas e responsável no card."
              title="Quadro de tarefas"
            >
              <TarefasQuadroMockup />
            </Variant>
            <Variant
              chosen
              label="aspiracional"
              note="Criar trabalho conversando no WhatsApp — em construção, ainda não existe no app."
              title="Assistente no WhatsApp"
            >
              <WhatsAppMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="Três dias em colunas altas, eventos posicionados por horário."
              title="Calendário"
            >
              <CalendarioMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="Arquivos com versão certa e miniaturas reais (render como capa)."
              title="Arquivos"
            >
              <ArquivosMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="O projeto pelos olhos do cliente, com aprovação pendente."
              title="Portal do cliente"
            >
              <PortalMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="Formato mobile maior, com marca do escritório, respostas e referências do cliente."
              title="Briefing por link"
            >
              <BriefingMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="A agenda de visitas do projeto, com a próxima em destaque."
              title="Agenda de visitas"
            >
              <VisitasAgendaMockup />
            </Variant>
            {/* Round 6 — built for the homepage's bentos, which needed a
             * real screen in every slot that used to hold abstract bars. */}
            <Variant
              chosen
              label="novo"
              note="A ficha do contato com tudo o que já aconteceu com ele — fecha a história do funil."
              title="Cliente com histórico"
            >
              <ClienteContextoMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="O editor colaborativo: tabela de especificação e comentário no ponto do texto."
              title="Documento"
            >
              <DocumentoMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="A terceira face do mesmo motor de documentos: cláusulas, parcelas e assinatura."
              title="Contrato"
            >
              <ContratoMockup />
            </Variant>
            <Variant
              chosen
              label="novo"
              note="O relatório mensal com fotos reais da obra e os canais de envio."
              title="Relatório mensal"
            >
              <RelatorioMockup />
            </Variant>
            <Variant
              chosen
              label="aspiracional"
              note="A caixa do escritório com a fatura já lida do anexo (par do WhatsApp na seção Canais)."
              title="E-mail do escritório"
            >
              <EmailMockup />
            </Variant>
          </Concept>

          <Concept
            intro="O grande mockup do hero — hoje um shell interativo com dez abas. Três direções em tamanho real para escolher; a vencedora ganha o tratamento interativo (abas que trocam sozinhas) na integração. Todas estáticas por enquanto."
            title="07 · O mockup principal"
          >
            <Variant
              className="md:col-span-2 xl:col-span-3"
              label="atual"
              note="O shell atual: bom, mas a navegação e o conteúdo divergem do app real (Financeiro, feed) e a home é genérica."
              title="Shell interativo de hoje"
            >
              <div className="mx-auto max-w-[1000px]">
                <AppMockup className="rounded-[20px]" />
              </div>
            </Variant>
            <Variant
              chosen
              className="md:col-span-2 xl:col-span-3"
              label="variação"
              note="Fiel ao app de verdade: rail claro, grupos Trabalho/Funil/Biblioteca, e o Início real — KPIs, funil por estágio, sprint e Minhas tarefas."
              title="A · Fiel ao app"
            >
              <div className="mx-auto max-w-[1000px]">
                <AppFielMockup />
              </div>
            </Variant>
            <Variant
              className="md:col-span-2 xl:col-span-3"
              label="variação"
              note="O produto vivendo no navegador, aberto na página do projeto — fotos da obra, visitas e a barra de progresso da etapa."
              title="B · No navegador, dentro do projeto"
            >
              <div className="mx-auto max-w-[1000px]">
                <AppNavegadorMockup />
              </div>
            </Variant>
            <Variant
              className="md:col-span-2 xl:col-span-3"
              label="variação"
              note="Escritório + canteiro em uma imagem: o quadro de tarefas no desktop e a visita técnica no celular, sobrepostos."
              title="C · Escritório + canteiro"
            >
              <div className="mx-auto max-w-[1000px]">
                <AppDuoMockup />
              </div>
            </Variant>
            <Variant
              chosen
              className="md:col-span-2 xl:col-span-3"
              label="variação"
              note="A montagem final, já no ar em /lp/prancheta: o shell da direção A com as dez páginas vencedoras da seção 08, ciclando sozinhas até o clique."
              title="Montagem final · A + páginas escolhidas"
            >
              <div className="mx-auto max-w-[1000px]">
                <WorkspaceMockup />
              </div>
            </Variant>
          </Concept>

          <Concept
            intro="As dez páginas que vivem dentro do mockup principal — cada uma com o estado atual e pelo menos duas variações. Todas no tamanho do painel de conteúdo do mockup (820×520)."
            title="08 · As páginas internas"
          >
            {INNER_PAGES.map((page) => {
              const atual = APP_MOCKUP_SCREENS.find((item) => item.id === page.id)
              return (
                <div className="md:col-span-2 xl:col-span-3" key={page.id}>
                  <h3
                    className={cn(
                      'mb-8 border-[#29251e]/12 border-b pb-3 font-semibold text-[20px] tracking-[-0.01em]',
                      SERIF
                    )}
                  >
                    {page.title}
                  </h3>
                  <div className="space-y-12">
                    <Variant label={page.atualLabel} note={page.atualNote} title="Como está hoje">
                      <ScreenBox>{atual?.render()}</ScreenBox>
                    </Variant>
                    {page.variants.map((variant) => (
                      <Variant
                        key={variant.title}
                        label={variant.label}
                        note={variant.note}
                        title={variant.title}
                      >
                        <ScreenBox>
                          <variant.C />
                        </ScreenBox>
                      </Variant>
                    ))}
                  </div>
                </div>
              )
            })}
          </Concept>
        </main>
      </PranchetaPage>
    </MarketingLocaleProvider>
  )
}
